/*
 *  Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
 *
 *  Licensed under the Apache License, Version 2.0 (the "License").
 *  You may not use this file except in compliance with the License.
 *  A copy of the License is located at
 *
 *  http://aws.amazon.com/apache2.0
 *
 *  or in the "license" file accompanying this file. This file is distributed
 *  on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 *  express or implied. See the License for the specific language governing
 *  permissions and limitations under the License.
 */
import _ from 'lodash';
import React from 'react';
import { decorate, computed } from 'mobx';
import { observer, inject } from 'mobx-react';
import { withRouter } from 'react-router-dom';
import { Line } from 'react-chartjs-2';
import { Icon } from 'semantic-ui-react';

import { isStoreLoading, isStoreError, isStoreReady } from '@amzn/base-ui/dist/models/BaseStore';
import { swallowError, nicePrice, niceDate } from '@amzn/base-ui/dist/helpers/utils';
import ErrorBox from '@amzn/base-ui/dist/parts/helpers/ErrorBox';
import ProgressPlaceHolder from '@amzn/base-ui/dist/parts/helpers/BasicProgressPlaceholder';

// expected props
// - envId (via prop)
// - scEnvironmentCostsStore  (via injection)
// - scEnvironmentsStore (via injection)
class ScEnvironmentCostTable extends React.Component {
  componentDidMount() {
    const costStore = this.getEnvCostStore();
    if (!isStoreReady(costStore) && !isStoreError(costStore)) {
      swallowError(costStore.load());
    }

    costStore.startHeartbeat();
  }

  componentWillUnmount() {
    const costStore = this.getEnvCostStore();
    costStore.stopHeartbeat();
  }

  get costsStore() {
    return this.props.scEnvironmentCostsStore;
  }

  get envsStore() {
    return this.props.scEnvironmentsStore;
  }

  get envId() {
    return this.props.envId;
  }

  getEnvCostStore() {
    const costsStore = this.costsStore;
    const envId = this.envId;
    return costsStore.getScEnvironmentCostStore(envId);
  }

  getEnvironment() {
    const store = this.envsStore.getScEnvironmentStore(this.envId);
    if (store && isStoreReady(store)) {
      return store.scEnvironment;
    }
    return null;
  }

  // Chart.js v2 configuration with RMIT colors
  getChartOptions() {
    return {
      responsive: true,
      maintainAspectRatio: false,
      legend: {
        display: false,
      },
      tooltips: {
        backgroundColor: '#1f2937',
        titleFontColor: '#f9fafb',
        bodyFontColor: '#f9fafb',
        xPadding: 12,
        yPadding: 12,
        cornerRadius: 8,
        callbacks: {
          title: (tooltipItems) => {
            const date = tooltipItems[0]?.xLabel;
            return date ? niceDate(date) : '';
          },
          label: (tooltipItem) => `$${nicePrice(tooltipItem.yLabel)} USD`
        }
      },
      scales: {
        xAxes: [{
          scaleLabel: { display: true, labelString: 'Date', fontColor: '#374151' },
          gridLines: { display: false },
          ticks: {
            fontColor: '#6b7280',
          }
        }],
        yAxes: [{
          scaleLabel: { display: true, labelString: 'Consumed Credits', fontColor: '#374151' },
          ticks: {
            beginAtZero: true,
            min: 0,
            fontColor: '#6b7280',
            callback: (value) => `$${value}`
          },
          gridLines: { color: '#f3f4f6' }
        }]
      }
    };
  }

  getChartData(list) {
    // Get environment creation date to filter data
    const env = this.getEnvironment();
    const createdAt = env?.createdAt ? new Date(env.createdAt) : null;
    
    // Sort list by date (oldest first) for proper graph display
    let sortedList = [...list].sort((a, b) => new Date(a.date) - new Date(b.date));
    
    // Filter to only show data from creation date onwards
    if (createdAt) {
      // Set to start of day for comparison
      const creationDay = new Date(createdAt.getFullYear(), createdAt.getMonth(), createdAt.getDate());
      sortedList = sortedList.filter(item => {
        const itemDate = new Date(item.date);
        return itemDate >= creationDay;
      });
    }
    
    const dates = sortedList.map(item => item.date);
    const amounts = sortedList.map(item => item.amount);

    return {
      labels: dates,
      datasets: [{
        label: 'Daily Cost',
        data: amounts,
        borderColor: '#2563eb', // RMIT blue
        backgroundColor: 'rgba(37, 99, 235, 0.1)',
        fill: true,
        tension: 0.3,
        pointRadius: 4,
        pointHoverRadius: 6,
        pointBackgroundColor: '#2563eb',
        pointBorderColor: '#ffffff',
        pointBorderWidth: 2
      }]
    };
  }

  renderInfoMessage() {
    return (
      <div className="workspace-cost-graph-info">
        <Icon name="info circle" />
        <span>AWS cost information is updated with a 24-hour delay.</span>
      </div>
    );
  }

  renderEmptyState() {
    return (
      <div className="workspace-cost-graph-empty">
        <Icon name="chart line" />
        <div className="workspace-cost-graph-empty-title">No Cost Data Available</div>
        <div className="workspace-cost-graph-empty-text">
          Cost information will appear here once the workspace has been running and AWS cost data is available.
        </div>
      </div>
    );
  }

  renderPendingState() {
    return (
      <div className="workspace-cost-graph-empty">
        <Icon name="clock outline" />
        <div className="workspace-cost-graph-empty-title">Cost Data Coming Soon</div>
        <div className="workspace-cost-graph-empty-text">
          This workspace was recently created. Cost data will become available from 24-48 hours after provisioning.
        </div>
      </div>
    );
  }

  renderCostGraph(list) {
    const chartData = this.getChartData(list);
    const chartOptions = this.getChartOptions();

    return (
      <div style={{ height: '300px' }}>
        <Line data={chartData} options={chartOptions} />
      </div>
    );
  }

  render() {
    const store = this.getEnvCostStore();
    let content = null;

    if (isStoreError(store)) {
      content = <ErrorBox error={store.error} className="p0" />;
    } else if (isStoreLoading(store)) {
      content = <ProgressPlaceHolder />;
    } else if (isStoreReady(store)) {
      content = this.renderMain();
    } else {
      content = null;
    }

    return <>{content}</>;
  }

  renderMain() {
    const costStore = this.getEnvCostStore();
    const cost = costStore.scEnvironmentCost;
    const list = cost.list;
    const isEmpty = _.isEmpty(list);
    
    // Check if filtered data (after creation date filter) is empty
    const chartData = this.getChartData(list);
    const isFilteredEmpty = _.isEmpty(chartData.labels);
    
    // If raw data exists but filtered is empty, workspace was just created
    const isPending = !isEmpty && isFilteredEmpty;

    return (
      <div className="workspace-cost-graph-container">
        {this.renderInfoMessage()}
        {!isEmpty && !isFilteredEmpty && this.renderCostGraph(list)}
        {isPending && this.renderPendingState()}
        {isEmpty && this.renderEmptyState()}
      </div>
    );
  }
}

// see https://medium.com/@mweststrate/mobx-4-better-simpler-faster-smaller-c1fbc08008da
decorate(ScEnvironmentCostTable, {
  costsStore: computed,
  envsStore: computed,
  envId: computed,
});

export default inject('scEnvironmentCostsStore', 'scEnvironmentsStore')(withRouter(observer(ScEnvironmentCostTable)));
